import type { ItemStubsResponseDTO } from '@tempo-io/roadmap-dto';
import * as React from 'react';

const PAGE_SIZE = 50;

export type DatasetPage = {
  pageIndex: number;
  topLevelIds: readonly number[];
};

export type DatasetPages = {
  pages: readonly DatasetPage[];
  pageIndexById: ReadonlyMap<number, number>;
  /**
   * Stable identifier for the page STRUCTURE (the top-level id sequence).
   * Becomes part of the page cache key so a filter/sort change — or a
   * top-level insert/delete — drops stale page data. It deliberately does NOT
   * change when a child is added or removed: the child is fetched incrementally
   * into its ancestor's existing page cache, instead of orphaning every page
   * (which would refetch and repaint all visible cells).
   */
  datasetSignature: string;
};

const EMPTY_PAGES: DatasetPages = {
  pages: [],
  pageIndexById: new Map(),
  datasetSignature: 'empty',
};

const findTopLevelAncestor = (
  id: number,
  parentById: ReadonlyMap<number, number | undefined>,
): number => {
  let current = id;
  while (true) {
    const parent = parentById.get(current);
    if (parent === undefined) return current;
    current = parent;
  }
};

export const useDatasetPages = (dataset?: ItemStubsResponseDTO): DatasetPages => {
  return React.useMemo(() => {
    if (!dataset || dataset.length === 0) return EMPTY_PAGES;

    const realStubs = dataset.filter((stub) => stub.id >= 0);
    if (realStubs.length === 0) return EMPTY_PAGES;

    const allIds = new Set<number>();
    for (const stub of realStubs) allIds.add(stub.id);

    const parentById = new Map<number, number | undefined>();
    const topLevelIds: number[] = [];
    for (const stub of realStubs) {
      const parentId = stub.parentId && allIds.has(stub.parentId) ? stub.parentId : undefined;
      parentById.set(stub.id, parentId);
      if (parentId === undefined) topLevelIds.push(stub.id);
    }

    const pages: DatasetPage[] = [];
    for (let i = 0; i < topLevelIds.length; i += PAGE_SIZE) {
      pages.push({
        pageIndex: pages.length,
        topLevelIds: topLevelIds.slice(i, i + PAGE_SIZE),
      });
    }

    // Every dataset id resolves to the page containing its top-level ancestor.
    // Children inherit their ancestor's page so expansion writes land in the
    // right cache entry.
    const topLevelToPage = new Map<number, number>();
    for (const page of pages) {
      for (const id of page.topLevelIds) topLevelToPage.set(id, page.pageIndex);
    }
    const pageIndexById = new Map<number, number>();
    for (const stub of realStubs) {
      const ancestor = findTopLevelAncestor(stub.id, parentById);
      const pageIndex = topLevelToPage.get(ancestor);
      if (pageIndex !== undefined) pageIndexById.set(stub.id, pageIndex);
    }

    // Order-sensitive hash of the top-level id sequence (child add/remove leaves
    // it untouched). An unsorted reorder reuses loaded rows via carryForwardPageCache.
    let hash = topLevelIds.length >>> 0;
    for (const id of topLevelIds) {
      hash = (hash * 31 + id) >>> 0;
    }
    const datasetSignature = hash.toString(36);

    return { pages, pageIndexById, datasetSignature };
  }, [dataset]);
};
